import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { useAuth } from '../../../auth/AuthProvider';
import { DataTable } from '../../../components/ui/DataTable';
import { DeleteOrganizationDialog } from './DeleteOrganizationDialog';
import { canAccessProviderAdmin } from './providerAdminAccess';

export function OrganizationsPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [notice, setNotice] = useState(null);

  const allowed = canAccessProviderAdmin(session);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await providerAdminApi.listOrganizations({ page: 1, page_size: 100 });
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load organizations');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) {
      load();
    } else {
      setLoading(false);
    }
  }, [allowed, load]);

  if (!allowed) {
    return (
      <p className="text-sm text-eorig-muted">
        Provider Admin access is required to view organizations.
      </p>
    );
  }

  const columns = [
    { key: 'name', header: 'Name' },
    { key: 'code', header: 'Code' },
    { key: 'org_type', header: 'Type' },
    { key: 'status', header: 'Status' },
    { key: 'contact_email', header: 'Contact' },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <button
          type="button"
          aria-label={`Delete ${row.name}`}
          onClick={(event) => {
            event.stopPropagation();
            setNotice(null);
            setPendingDelete(row);
          }}
          className="rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
        >
          Delete
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-eorig-muted">
          Organization setup is Stage 0. Add an institution before people, connections, or Digitize.
        </p>
        <Link
          to="/provider/admin/organizations/new"
          className="rounded-xl bg-[#003A70] px-4 py-2 text-sm font-semibold text-white"
        >
          Add organization
        </Link>
      </div>
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="text-sm text-emerald-700" role="status">
          {notice}
        </p>
      ) : null}
      {loading ? (
        <p className="text-sm text-eorig-muted">Loading organizations…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-eorig-border bg-white p-6 text-sm text-eorig-muted">
          No organizations yet. Add organization to start configuration.
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/provider/admin/organizations/${row.id}`)}
        />
      )}
      <DeleteOrganizationDialog
        org={pendingDelete}
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onDeleted={(deleted) => {
          setPendingDelete(null);
          setNotice(`${deleted.name} was deleted.`);
          load();
        }}
      />
    </div>
  );
}
